import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import KnowledgeBaseList from '@common-components/knowledge-base/list/KnowledgeBaseList';
import AppView from '../../components/app-view';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import BTButton from '../../components/bt-button/bt-button';
import { AnalyticsService } from '@common/services/analytics-service';
import { DBKnowledgeBaseThunks } from '@common-reducers/DBServiceThunks';

const KnowledgeBaseView = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const analytics = new AnalyticsService();
  
  // We need the count for analytics, but maybe we can skip it or get it differently to avoid duplicating the selector logic
  // For now, let's just track the event without the count or get it if easy
  const knowledgeBaseCount = useAppSelector(state => state.DBKnowledgeBaseReducer?.ids?.length || 0);

  const handleCreateNew = useCallback(async () => {
    // Track knowledge base creation attempt
    analytics.event('kb_creation_initiated', {
      category: 'knowledge_base_management',
      action: 'create_new_kb',
      total_kbs: knowledgeBaseCount
    });

    setIsCreating(true);
    try {
      const newKnowledgeBase = {
        name: t('knowledgeBase.newKnowledgeBase'),
        description: t('knowledgeBase.defaultDescription'),
        status: 'active' as const,
        documentCount: 0,
        totalSize: 0,
        documentTypes: {
          pdf: 0,
          word: 0,
          excel: 0
        },
        associatedBots: [],
        lastUpdated: new Date()
      };

      const result = await dispatch(DBKnowledgeBaseThunks.create(newKnowledgeBase)).unwrap();
      if (result._id) {
        // Track successful creation
        analytics.event('kb_creation_success', {
          category: 'knowledge_base_management',
          action: 'kb_created',
          kb_id: result._id,
          kb_name: result.name
        });
        
        navigate(`/knowledge-base/${result._id}`);
      }
    } catch (error) {
      console.error('Failed to create knowledge base:', error);
      
      // Track creation failure
      analytics.event('kb_creation_error', {
        category: 'knowledge_base_management',
        action: 'creation_failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsCreating(false);
    }
  }, [dispatch, navigate, analytics, knowledgeBaseCount, t]);

  const headerActions = (
    <BTButton
      onClick={handleCreateNew}
      startIcon={<Plus className="w-5 h-5" />}
      btvariant="primary"
      disabled={isCreating}
      loading={isCreating}
    >
      {t('knowledgeBase.newKnowledgeBase')}
    </BTButton>
  );

  return (
    <AppView 
      title={t('knowledgeBase.title')} 
      activeExtensionView="knowledge-base"
      showHeader={true}
      headerActions={headerActions}
      showStatusItems={false}
    >
      <Box sx={{ 
        width: '100%',
        height: '100%',
        bgcolor: 'background.default'
      }}>
        <KnowledgeBaseList />
      </Box>
    </AppView>
  );
};

export default KnowledgeBaseView; 